import { useState, useEffect } from 'react';
import { Award, Plus, Pencil, Archive } from 'lucide-react';
import {
  fetchAllBadges, createBadge, updateBadge, retireBadge,
  getDisplayErrorMessage,
  type BadgeDefinition,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './BadgesPage.module.css';
import formStyles from '@/components/ui/Form.module.css';

const TRIGGER_LABELS: Record<string, string> = {
  session_count: 'Sessions',
  kwh_delivered: 'kWh',
  streak_days: 'Streak (days)',
  spending_total: 'Spending (cents)',
  referral_count: 'Referrals',
  special_event: 'Special',
};

const RARITY_COLORS: Record<string, string> = {
  common: '#aaaaaa',
  rare: '#3498db',
  epic: '#9b59b6',
  legendary: '#f39c12',
};

export function BadgesPage() {
  useRecentRoute('Badges');
  const [badges, setBadges] = useState<BadgeDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BadgeDefinition | undefined>();
  const [filterRarity, setFilterRarity] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setBadges(await fetchAllBadges());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreate = () => { setEditing(undefined); setModalOpen(true); };
  const onEdit = (b: BadgeDefinition) => { setEditing(b); setModalOpen(true); };

  const onRetire = async (b: BadgeDefinition) => {
    if (!confirm(`Retire badge '${b.name}'? Existing awards will remain but no new ones will be granted.`)) return;
    try {
      await retireBadge(b.badge_id);
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to retire');
    }
  };

  const filtered = filterRarity ? badges.filter((b) => b.rarity === filterRarity) : badges;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Award size={18} /></div>
          <div>
            <h1 className={styles.title}>Badges</h1>
            <p className={styles.subtitle}>{badges.length} badge definitions</p>
          </div>
        </div>
        <button className={styles.primaryBtn} onClick={onCreate}>
          <Plus size={16} /> New Badge
        </button>
      </div>

      <div className={styles.filterRow}>
        <select className={styles.select} value={filterRarity} onChange={(e) => setFilterRarity(e.target.value)}>
          <option value="">All rarities</option>
          <option value="common">Common</option>
          <option value="rare">Rare</option>
          <option value="epic">Epic</option>
          <option value="legendary">Legendary</option>
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading...</div>
      ) : (
        <div className={styles.grid}>
          {filtered.map((b) => (
            <div key={b._id} className={`${styles.card} ${!b.is_active ? styles.retired : ''}`}>
              <div className={styles.cardHeader}>
                <span className={styles.rarityBadge} style={{ background: `${RARITY_COLORS[b.rarity]}20`, color: RARITY_COLORS[b.rarity], borderColor: `${RARITY_COLORS[b.rarity]}50` }}>
                  {b.rarity}
                </span>
                {!b.is_active && <span className={styles.retiredBadge}>RETIRED</span>}
              </div>
              <h3 className={styles.cardName}>{b.name}</h3>
              <p className={styles.cardDesc}>{b.description}</p>
              <div className={styles.cardMeta}>
                <span className={styles.metaItem}>
                  <strong>{TRIGGER_LABELS[b.trigger_type] ?? b.trigger_type}</strong>
                  {b.trigger_type !== 'special_event' ? `: ${b.threshold.toLocaleString()}` : ''}
                </span>
                {b.awarded_count !== undefined && (
                  <span className={styles.metaItem}>{b.awarded_count} earned</span>
                )}
              </div>
              <div className={styles.cardActions}>
                <button onClick={() => onEdit(b)} title="Edit"><Pencil size={14} /></button>
                {b.is_active && (
                  <button onClick={() => onRetire(b)} title="Retire" className={styles.retireBtn}>
                    <Archive size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Badge' : 'New Badge'}
        size="md"
      >
        <BadgeForm
          existing={editing}
          onSuccess={() => { setModalOpen(false); load(); }}
          onCancel={() => setModalOpen(false)}
        />
      </Modal>
    </div>
  );
}

interface BadgeFormProps {
  existing?: BadgeDefinition;
  onSuccess: () => void;
  onCancel: () => void;
}

function BadgeForm({ existing, onSuccess, onCancel }: BadgeFormProps) {
  const [badgeId, setBadgeId] = useState(existing?.badge_id ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [triggerType, setTriggerType] = useState<BadgeDefinition['trigger_type']>(existing?.trigger_type ?? 'session_count');
  const [threshold, setThreshold] = useState(String(existing?.threshold ?? 1));
  const [rarity, setRarity] = useState<BadgeDefinition['rarity']>(existing?.rarity ?? 'common');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr('');
    try {
      const body = {
        badge_id: badgeId.trim(),
        name: name.trim(),
        description: description.trim(),
        trigger_type: triggerType,
        threshold: parseFloat(threshold) || 0,
        rarity,
        is_active: true,
      };
      if (existing) {
        await updateBadge(existing.badge_id, body);
      } else {
        await createBadge(body);
      }
      onSuccess();
    } catch (e) {
      // Surface VALIDATION_ERROR (slug uniqueness, rule-payload shape)
      // verbatim from the server; fall back to generic for other codes.
      setErr(await getDisplayErrorMessage(e, 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={formStyles.form} onSubmit={handleSubmit}>
      {err && <div className={formStyles.formError}>{err}</div>}

      <div className={formStyles.field}>
        <label className={`${formStyles.label} ${formStyles.required}`}>Badge ID</label>
        <input
          className={formStyles.input}
          value={badgeId}
          onChange={(e) => setBadgeId(e.target.value)}
          placeholder="lowercase_with_underscores"
          pattern="^[a-z0-9_]+$"
          required
          maxLength={60}
          disabled={!!existing}
        />
        <p className={formStyles.hint}>Stable identifier — cannot be changed after creation</p>
      </div>

      <div className={formStyles.field}>
        <label className={`${formStyles.label} ${formStyles.required}`}>Name</label>
        <input className={formStyles.input} value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} />
      </div>

      <div className={formStyles.field}>
        <label className={`${formStyles.label} ${formStyles.required}`}>Description</label>
        <input className={formStyles.input} value={description} onChange={(e) => setDescription(e.target.value)} required maxLength={500} />
      </div>

      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Trigger</label>
          <select className={formStyles.select} value={triggerType} onChange={(e) => setTriggerType(e.target.value as BadgeDefinition['trigger_type'])}>
            <option value="session_count">Session count</option>
            <option value="kwh_delivered">kWh delivered</option>
            <option value="streak_days">Streak days</option>
            <option value="spending_total">Spending total (cents)</option>
            <option value="referral_count">Referral count</option>
            <option value="special_event">Special event (manual)</option>
          </select>
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Threshold</label>
          <input
            className={formStyles.input}
            type="number"
            min={0}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            disabled={triggerType === 'special_event'}
          />
        </div>
      </div>

      <div className={formStyles.field}>
        <label className={`${formStyles.label} ${formStyles.required}`}>Rarity</label>
        <select className={formStyles.select} value={rarity} onChange={(e) => setRarity(e.target.value as BadgeDefinition['rarity'])}>
          <option value="common">Common (+25 pts)</option>
          <option value="rare">Rare (+100 pts)</option>
          <option value="epic">Epic (+250 pts)</option>
          <option value="legendary">Legendary (+1000 pts)</option>
        </select>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button type="button" className={formStyles.btnSecondary} onClick={onCancel} disabled={submitting}>Cancel</button>
        <button type="submit" className={formStyles.btnPrimary} disabled={submitting}>
          {submitting ? 'Saving...' : (existing ? 'Update' : 'Create')}
        </button>
      </div>
    </form>
  );
}
